import { inject } from '@angular/core';
import { CanActivateFn, UrlTree } from '@angular/router';
import { catchError, map, Observable, of, switchMap } from 'rxjs';

import { AUTH_ROUTES } from '../../auth/constants/auth-routes.constants';
import { OrganizationType } from '../../auth/interfaces';
import { AuthNavigationService } from '../../auth/services/auth-navigation/auth-navigation.service';
import { SponsorOnboardingService } from '../../onboarding/sponsor-onboarding/services/sponsor-onboarding.service';
import { AuthBootstrapService } from '../services/auth-bootstrap.service';
import { AppRouteNavigationService } from '../services/app-route-navigation.service';
import { AuthService } from '../../auth/services/auth/auth.service';

export const rootRedirectGuard: CanActivateFn = (): Observable<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const authBootstrap = inject(AuthBootstrapService);
  const appRouteNavigation = inject(AppRouteNavigationService);
  const authNavigation = inject(AuthNavigationService);
  const sponsorOnboarding = inject(SponsorOnboardingService);

  return authBootstrap.ensureSessionReady().pipe(
    switchMap((session): Observable<UrlTree> => {
      if (authBootstrap.isGuest(session)) {
        return of(appRouteNavigation.createUrlTree(AUTH_ROUTES.login));
      }

      if (!authBootstrap.canAccessProtectedRoutes(session)) {
        return of(appRouteNavigation.createUrlTree(AUTH_ROUTES.login));
      }

      if (session.organization?.organization_type === OrganizationType.Entity) {
        if (authService.isOnboardedFromSession()) {
          return of(authNavigation.createAuthenticatedHomeUrlTree());
        }
        return authService.getOnboardingStatus().pipe(
          map((response) => {
            if (response.success && response.data.completed === true) {
              return authNavigation.createAuthenticatedHomeUrlTree();
            }
            return appRouteNavigation.createUrlTree(AUTH_ROUTES.onboardingChoice);
          }),
          catchError(() => of(appRouteNavigation.createUrlTree(AUTH_ROUTES.onboardingChoice))),
        );
      }

      if (session.organization?.organization_type === OrganizationType.Sponsor) {
        if (authService.hasPendingEmailVerificationStatus()) {
          return of(appRouteNavigation.createUrlTree(AUTH_ROUTES.sponsorEmailVerification));
        }
        if (authService.isSponsorAwaitingAdminApproval()) {
          return of(appRouteNavigation.createUrlTree(AUTH_ROUTES.sponsorPendingAdminVerification));
        }
        if (authService.isOnboardedFromSession()) {
          return of(appRouteNavigation.createUrlTree(AUTH_ROUTES.sponsorHome));
        }
        return sponsorOnboarding.ensureStatusLoaded().pipe(
          map(
            () =>
              appRouteNavigation.createUrlTree(sponsorOnboarding.getResumeStepPath()),
          ),
          catchError(() => of(appRouteNavigation.createUrlTree(AUTH_ROUTES.onboardingSponsor))),
        );
      }

      return of(appRouteNavigation.createUrlTree(AUTH_ROUTES.login));
    }),
    catchError(() => of(appRouteNavigation.createUrlTree(AUTH_ROUTES.login))),
  );
};
